<script>
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import axios from 'axios';

const SCRIPT_OPEN = '\x3cscript\x3e';
const SCRIPT_CLOSE = '\x3c\x2fscript\x3e';
const DEFAULT_POSITION = { side: 65, bottom: 75 };
const SUPPORTED_EYE_CATCHER_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
const EYE_CATCHER_UPLOAD_ACCEPT = '.png,.jpg,.jpeg,.gif,.webp';

export default {
  components: {},
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    const enabled = !!this.inbox?.eye_catcher_enabled;
    const imageUrl = (this.inbox?.eye_catcher_image_url || '').toString();
    const source = (this.inbox?.eye_catcher_source || 'gallery').toString();
    const position =
      this.inbox?.eye_catcher_position &&
      typeof this.inbox.eye_catcher_position === 'object'
        ? { ...DEFAULT_POSITION, ...this.inbox.eye_catcher_position }
        : { ...DEFAULT_POSITION };

    return {
      state: {
        enabled,
        imageUrl,
        source,
        position,
      },
      originalSnapshot: '',
      galleryItems: [],
      galleryError: '',
      isUploadOpen: true,
      isGalleryOpen: true,
      isLoadingGallery: false,
      isUploading: false,
      deletingGalleryItems: {},
      isSaving: false,
      isDragging: false,
      dragStart: { x: 0, y: 0, side: 0, bottom: 0 },
      previewDocHtml: '',
      uploadAccept: EYE_CATCHER_UPLOAD_ACCEPT,
    };
  },
  computed: {
    isDirty() {
      return this.snapshot() !== this.originalSnapshot;
    },
    saveDisabled() {
      return this.isSaving || !this.isDirty;
    },
    previewBubbleSide() {
      return this.inbox?.widget_theme_config?.sideSpacing ?? 0;
    },
    isLeftPosition() {
      const rawPosition =
        this.inbox?.position ||
        this.inbox?.widget_theme_config?.position ||
        'right';
      return String(rawPosition).toLowerCase() === 'left';
    },
    livePreviewBaseUrl() {
      const script = this.inbox.web_widget_script || '';
      const match = script.match(/var\s+BASE_URL\s*=\s*['"]([^'"]+)['"]/);
      return match?.[1] || window.location.origin;
    },
    livePreviewWebsiteToken() {
      return this.inbox.website_token || '';
    },
    liveChatwootSettings() {
      const isMidnight = this.inbox.widget_theme_preset === 'midnight';
      const themeConfig = this.inbox?.widget_theme_config || {};
      return {
        locale: this.inbox.widget_language || 'en',
        position: this.inbox.position || 'right',
        type: 'standard',
        allowThemeOverride: true,
        darkMode: isMidnight ? 'dark' : 'light',
        themePreset: this.inbox.widget_theme_preset || 'classic',
        widgetStyle: 'rounded',
        widgetColor: this.inbox.widget_color || '#0059e1',
        iconColor: '#ffffff',
        themeConfig: {
          ...themeConfig,
        },
        welcomeTitle: this.inbox.welcome_title || '',
        welcomeDescription: this.inbox.welcome_tagline || '',
        announcementText: this.inbox.announcement_text || '',
        announcementUrl: this.inbox.announcement_url || '',
        announcementBgColor: this.inbox.announcement_bg_color || '#242529',
        announcementTextColor: this.inbox.announcement_text_color || '#ffffff',
        hideMessageBubble: false,
        enableFileUpload: true,
        enableEmojiPicker: true,
        enableEndConversation: true,
        initialRoute: 'messages',
      };
    },
  },
  watch: {
    inbox: {
      immediate: true,
      handler(next, prev) {
        const nextId = next?.id;
        const prevId = prev?.id;
        if (nextId && nextId !== prevId) {
          this.resetChanges();
          this.fetchGallery();
          this.previewDocHtml = this.buildPreviewDoc();
        }
      },
    },
    state: {
      deep: true,
      handler() {
        this.pushSettingsToPreview();
      },
    },
  },
  mounted() {
    this.originalSnapshot = this.snapshot();
    this.fetchGallery();
    this.previewDocHtml = this.buildPreviewDoc();
  },
  beforeUnmount() {
    this.teardownDragListeners();
  },
  methods: {
    snapshot() {
      const { enabled, imageUrl, source, position } = this.state;
      return JSON.stringify({
        enabled: !!enabled,
        imageUrl: (imageUrl || '').toString(),
        source: (source || '').toString(),
        position: {
          side: Number(position?.side ?? DEFAULT_POSITION.side),
          bottom: Number(position?.bottom ?? DEFAULT_POSITION.bottom),
        },
      });
    },
    async fetchGallery() {
      if (!this.inbox?.id) return;
      this.isLoadingGallery = true;
      try {
        const { data } = await InboxesAPI.getEyeCatcherGallery(this.inbox.id);
        this.galleryItems = this.dedupeGalleryItems(data?.items || []);
        this.galleryError = '';

        if (!this.galleryItems.length) {
          const resp = await axios.get('/eye_catchers_gallery');
          this.galleryItems = this.dedupeGalleryItems(resp?.data?.items || []);
        }

        this.applyDefaultGallerySelection();
      } catch (e) {
        this.galleryItems = [];
        this.galleryError =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          'Failed to load gallery';
      } finally {
        this.isLoadingGallery = false;
      }
    },
    dedupeGalleryItems(items = []) {
      const list = Array.isArray(items) ? items : [];
      const seen = new Set();
      return list.filter(item => {
        const filename = (item?.filename || '').toString().trim().toLowerCase();
        const url = (item?.url || '').toString().trim();
        const key = filename || url;
        if (!key) return false;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    extractFilenameFromUrl(url = '') {
      const raw = (url || '').toString();
      if (!raw) return '';
      try {
        const withoutHash = raw.split('#')[0];
        const withoutQuery = withoutHash.split('?')[0];
        const lastPart = withoutQuery.split('/').pop() || '';
        return decodeURIComponent(lastPart);
      } catch (error) {
        return '';
      }
    },
    formatFileNameList(names = []) {
      const cleanNames = (Array.isArray(names) ? names : [])
        .map(name => (name || '').toString().trim())
        .filter(Boolean);
      if (!cleanNames.length) return '';
      if (cleanNames.length <= 3) return cleanNames.join(', ');
      return `${cleanNames.slice(0, 3).join(', ')} +${cleanNames.length - 3} more`;
    },
    extractUploadErrorMessage(error, fallback = 'Failed to upload image') {
      return (
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        fallback
      );
    },
    upsertGalleryItem(item = {}) {
      const rawFilename = (item?.filename || '').toString().trim();
      const rawUrl = (item?.url || '').toString().trim();
      const filename = rawFilename || this.extractFilenameFromUrl(rawUrl);
      if (!filename || !rawUrl) return;

      const targetKey = filename.toLowerCase();
      const filtered = this.galleryItems.filter(existing => {
        const key = (existing?.filename || '').toString().trim().toLowerCase();
        return key !== targetKey;
      });

      this.galleryItems = this.dedupeGalleryItems([
        { filename, url: rawUrl },
        ...filtered,
      ]);
    },
    removeGalleryItemLocally(filename) {
      const target = (filename || '').toString().trim().toLowerCase();
      if (!target) return;

      const remaining = this.galleryItems.filter(item => {
        const key = (item?.filename || '').toString().trim().toLowerCase();
        return key !== target;
      });
      this.galleryItems = remaining;

      const selectedFilename = this.extractFilenameFromUrl(this.state.imageUrl)
        .toString()
        .trim()
        .toLowerCase();
      if (selectedFilename !== target) return;

      const next = remaining[0];
      if (next?.url) {
        this.state.source = 'gallery';
        this.state.imageUrl = next.url;
        return;
      }

      this.state.source = 'gallery';
      this.state.imageUrl = '';
      this.state.enabled = false;
    },
    isDeletingGalleryItem(filename) {
      const key = (filename || '').toString();
      return !!this.deletingGalleryItems[key];
    },
    setDeletingGalleryItem(filename, isDeleting) {
      const key = (filename || '').toString();
      if (!key) return;
      const next = { ...this.deletingGalleryItems };
      if (isDeleting) {
        next[key] = true;
      } else {
        delete next[key];
      }
      this.deletingGalleryItems = next;
    },
    applyDefaultGallerySelection() {
      if (this.state.imageUrl) return;
      if (!this.galleryItems.length) return;
      const preferred = this.galleryItems.find(it =>
        (it?.filename || '').toString().toLowerCase().includes('new110')
      );
      const first = preferred || this.galleryItems[0];
      if (!first?.url) return;
      this.state.source = 'gallery';
      this.state.imageUrl = first.url;
    },
    onToggleEnabled(next) {
      this.state.enabled = !!next;
    },
    selectGalleryItem(item) {
      if (!item?.url) return;
      this.state.enabled = true;
      this.state.source = 'gallery';
      this.state.imageUrl = item.url;
    },
    async onUploadFileChange(e) {
      const files = Array.from(e?.target?.files || []);
      if (!files.length || !this.inbox?.id) return;

      const validFiles = [];
      const invalidFiles = [];
      files.forEach(file => {
        const fileExt = (file?.name || '')
          .toString()
          .split('.')
          .pop()
          ?.toLowerCase();
        if (SUPPORTED_EYE_CATCHER_EXTENSIONS.includes(fileExt)) {
          validFiles.push(file);
        } else {
          invalidFiles.push((file?.name || '').toString() || 'Unnamed file');
        }
      });

      if (!validFiles.length) {
        useAlert(
          'Unsupported file type. Please upload png, jpg, jpeg, gif, or webp.'
        );
        if (e?.target) e.target.value = '';
        return;
      }

      this.isUploading = true;
      let latestUploadedItem = null;
      let successCount = 0;
      const failedUploads = [];

      try {
        for (const file of validFiles) {
          try {
            const response = await InboxesAPI.uploadEyeCatcher(
              this.inbox.id,
              file
            );
            const uploadedImageUrl =
              (
                response?.data?.channel?.eye_catcher_image_url ||
                response?.data?.eye_catcher_image_url ||
                ''
              ).toString() || '';
            const uploadedFilename =
              this.extractFilenameFromUrl(uploadedImageUrl) ||
              (file?.name || '').toString() ||
              'eye-catcher';

            if (!uploadedImageUrl) {
              failedUploads.push({
                name: (file?.name || '').toString() || 'Unnamed file',
                message: 'Upload finished without returning an image URL',
              });
              continue;
            }

            latestUploadedItem = {
              filename: uploadedFilename,
              url: uploadedImageUrl,
            };
            successCount += 1;
            this.upsertGalleryItem(latestUploadedItem);
            this.state.enabled = true;
            this.state.source = 'upload';
            this.state.imageUrl = uploadedImageUrl;
          } catch (error) {
            failedUploads.push({
              name: (file?.name || '').toString() || 'Unnamed file',
              message: this.extractUploadErrorMessage(error),
            });
          }
        }

        if (successCount > 0) {
          await this.$store.dispatch('inboxes/get');
          const persistedImageUrl = (
            this.$store.getters['inboxes/getInbox'](this.inbox.id)
              ?.eye_catcher_image_url ||
            latestUploadedItem?.url ||
            this.state.imageUrl ||
            ''
          ).toString();

          this.state.enabled = true;
          this.state.source = 'upload';
          this.state.imageUrl = persistedImageUrl;

          if (persistedImageUrl) {
            this.upsertGalleryItem({
              filename:
                this.extractFilenameFromUrl(persistedImageUrl) ||
                latestUploadedItem?.filename ||
                'eye-catcher',
              url: persistedImageUrl,
            });
          }

          await this.fetchGallery();
        }

        if (!invalidFiles.length && !failedUploads.length && successCount === 1) {
          useAlert('Eye-catcher image uploaded');
          return;
        }

        if (!invalidFiles.length && !failedUploads.length && successCount > 1) {
          useAlert(`${successCount} eye-catcher images uploaded`);
          return;
        }

        const uploadMessages = [];
        if (successCount > 0) {
          uploadMessages.push(
            `Uploaded ${successCount} image${successCount > 1 ? 's' : ''}.`
          );
        }
        if (invalidFiles.length) {
          uploadMessages.push(
            `Skipped unsupported files: ${this.formatFileNameList(invalidFiles)}.`
          );
        }
        if (failedUploads.length === 1) {
          uploadMessages.push(
            `Failed to upload ${failedUploads[0].name}: ${failedUploads[0].message}.`
          );
        } else if (failedUploads.length > 1) {
          uploadMessages.push(`${failedUploads.length} files failed to upload.`);
        }

        useAlert(uploadMessages.join(' ') || 'Failed to upload image');
      } finally {
        this.isUploading = false;
        if (e?.target) e.target.value = '';
      }
    },
    async onDeleteGalleryItem(item) {
      const filename = (item?.filename || '').toString().trim();
      if (!filename || !this.inbox?.id) return;
      if (this.isDeletingGalleryItem(filename)) return;

      const confirmed = window.confirm(`Delete "${filename}" from gallery?`);
      if (!confirmed) return;

      this.setDeletingGalleryItem(filename, true);
      try {
        await InboxesAPI.deleteEyeCatcherGalleryItem(this.inbox.id, filename);
        this.removeGalleryItemLocally(filename);
        useAlert('Eye-catcher deleted');
        this.fetchGallery();
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to delete eye-catcher';
        useAlert(message);
      } finally {
        this.setDeletingGalleryItem(filename, false);
      }
    },
    resetChanges() {
      const enabled = !!this.inbox?.eye_catcher_enabled;
      const imageUrl = (this.inbox?.eye_catcher_image_url || '').toString();
      const source = (this.inbox?.eye_catcher_source || 'gallery').toString();
      const position =
        this.inbox?.eye_catcher_position &&
        typeof this.inbox.eye_catcher_position === 'object'
          ? { ...DEFAULT_POSITION, ...this.inbox.eye_catcher_position }
          : { ...DEFAULT_POSITION };
      this.state = { enabled, imageUrl, source, position };
      this.originalSnapshot = this.snapshot();
    },
    buildPreviewDoc() {
      if (!this.livePreviewWebsiteToken) return '';
      const isMidnight = this.inbox.widget_theme_preset === 'midnight';
      const bg =
        isMidnight && this.inbox.widget_theme_config?.background
          ? this.inbox.widget_theme_config.background
          : isMidnight
            ? '#111111'
            : '#ffffff';
      const settingsJSON = JSON.stringify(this.liveChatwootSettings)
        .split('<')
        .join('\\u003c');
      const baseUrl = JSON.stringify(this.livePreviewBaseUrl);
      const token = JSON.stringify(this.livePreviewWebsiteToken);

      return (
        `<!doctype html><html><head>` +
        `<meta charset="utf-8"/>` +
        `<meta name="viewport" content="width=device-width,initial-scale=1"/>` +
        `<style>` +
        `html,body{margin:0;width:100%;height:100%;background:${bg};overflow:hidden;}` +
        `body{position:fixed;inset:0;overflow:hidden;}` +
        `.woot--bubble-holder{visibility:visible;transition:visibility 0s;}` +
        `.woot-widget-eye-catcher{display:none !important;}` +
        `</style>` +
        `</head><body>` +
        SCRIPT_OPEN +
        `window.wlabSettings=${settingsJSON};` +
        `window.addEventListener('message',function(e){` +
        `try{var d;if(typeof e.data==='string')d=JSON.parse(e.data);else d=e.data;` +
        `if(d&&d.type==='wlab-settings'){` +
        `var f=document.getElementById('chatwoot_live_chat_widget');if(!f)return;` +
        `f.contentWindow.postMessage('chatwoot-widget:'+JSON.stringify(` +
        `Object.assign({event:'config-set'},d.s,{locale:(d.s&&d.s.locale)||'en',showUnreadMessagesDialog:true,isPreviewUpdate:true})),'*');` +
        `if(d.s.darkMode)f.contentWindow.postMessage('chatwoot-widget:'+` +
        `JSON.stringify({event:'set-color-scheme',darkMode:d.s.darkMode}),'*');` +
        `}}catch(x){}});` +
        `(function(d,t){var B=${baseUrl},T=${token};` +
        `var g=d.createElement(t),s=d.getElementsByTagName(t)[0];` +
        `g.src=B+"/packs/js/sdk.js?v="+Date.now();g.async=true;` +
        `s.parentNode.insertBefore(g,s);` +
        `g.onload=function(){window.wlabSDK.run({websiteToken:T,baseUrl:B});` +
        `setTimeout(function(){setInterval(function(){` +
        `var wh=document.querySelector('.woot-widget-holder');` +
        `var bh=document.querySelector('.woot--bubble-holder');` +
        `if(!wh||!bh)return;` +
        `bh.style.visibility=wh.classList.contains('woot--hide')?'visible':'hidden';` +
        `},250);},1600);};` +
        `})(document,"script");` +
        SCRIPT_CLOSE +
        `</body></html>`
      );
    },
    pushSettingsToPreview() {
      const iframe = this.$refs.sdkFrame;
      if (!iframe?.contentWindow) return;
      try {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            type: 'wlab-settings',
            s: this.liveChatwootSettings,
          }),
          '*'
        );
      } catch (error) {
        // Ignore preview sync errors.
      }
    },
    openWebsitePreview() {
      const url =
        this.inbox.website_url || this.inbox.channel?.website_url || '';
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    async saveChanges() {
      if (this.saveDisabled) return;
      this.isSaving = true;
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          channel: {
            eye_catcher_enabled: this.state.enabled,
            eye_catcher_image_url: this.state.imageUrl,
            eye_catcher_source: this.state.source,
            eye_catcher_position: {
              side: Number(this.state.position?.side ?? DEFAULT_POSITION.side),
              bottom: Number(
                this.state.position?.bottom ?? DEFAULT_POSITION.bottom
              ),
            },
          },
        });
        await this.$store.dispatch('inboxes/get');
        this.originalSnapshot = this.snapshot();
        useAlert('Eye-catcher saved');
      } catch (error) {
        useAlert(error?.message || 'Failed to save');
      } finally {
        this.isSaving = false;
      }
    },
    toggleUploadOpen() {
      this.isUploadOpen = !this.isUploadOpen;
    },
    toggleGalleryOpen() {
      this.isGalleryOpen = !this.isGalleryOpen;
    },
    startDrag(ev) {
      if (!this.state.enabled || !this.state.imageUrl) return;
      this.isDragging = true;
      this.dragStart = {
        x: ev.clientX,
        y: ev.clientY,
        side: Number(this.state.position?.side ?? DEFAULT_POSITION.side),
        bottom: Number(this.state.position?.bottom ?? DEFAULT_POSITION.bottom),
      };
      this.setupDragListeners();
    },
    onDragMove(ev) {
      if (!this.isDragging) return;
      const dx = ev.clientX - this.dragStart.x;
      const dy = ev.clientY - this.dragStart.y;
      const nextSide = Math.max(
        0,
        this.isLeftPosition
          ? this.dragStart.side + dx
          : this.dragStart.side - dx
      );
      const nextBottom = Math.max(0, this.dragStart.bottom - dy);
      this.state.position = {
        ...this.state.position,
        side: Math.round(nextSide),
        bottom: Math.round(nextBottom),
      };
    },
    endDrag() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.teardownDragListeners();
    },
    setupDragListeners() {
      window.addEventListener('mousemove', this.onDragMove);
      window.addEventListener('mouseup', this.endDrag);
    },
    teardownDragListeners() {
      window.removeEventListener('mousemove', this.onDragMove);
      window.removeEventListener('mouseup', this.endDrag);
    },
  },
};
</script>

<template>
  <div class="ec-page">
    <div class="ec-layout">
      <!-- ── LEFT: config + fixed save ── -->
      <section class="ec-config">
        <header class="ec-config__header">
          <div class="ec-config__title">Eye-catcher</div>
        </header>

        <!-- scrollable body -->
        <div class="ec-config__body">
          <p class="ec-config__subtitle" data-testid="eye-catcher-info">
            Grab your website visitors' attention and encourage them to start a
            chat by showing an eye-catcher. Eye-catchers are only shown on
            desktop.
            <a
              class="ec-link"
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.livechat.com/help/more-chats-with-the-eye-catcher/"
              >Learn more</a
            >
          </p>
          <!-- toggle card -->
          <div class="ec-card">
            <div class="ec-card__row">
              <h5 class="ec-card__title">Show eye-catcher</h5>
              <button
                type="button"
                class="ec-toggle"
                :aria-pressed="state.enabled ? 'true' : 'false'"
                aria-label="Toggle eye-catcher"
                @click="onToggleEnabled(!state.enabled)"
              >
                <span
                  class="ec-toggle__track"
                  :class="{ 'ec-toggle__track--on': state.enabled }"
                >
                  <span class="ec-toggle__knob" />
                </span>
              </button>
            </div>
            <p class="ec-card__hint">
              Personalize your eye-catcher to increase chat engagement.
            </p>
          </div>

          <!-- upload accordion -->
          <div class="ec-accordion">
            <button
              class="ec-accordion__hd"
              type="button"
              @click="toggleUploadOpen"
            >
              <span class="ec-accordion__left">
                <span class="i-lucide-upload ec-accordion__icon" />
                <span class="ec-accordion__label">Upload your images</span>
              </span>
              <span
                class="i-lucide-chevron-right ec-accordion__chev"
                :class="{ 'ec-accordion__chev--open': isUploadOpen }"
              />
            </button>
            <div v-show="isUploadOpen" class="ec-accordion__bd">
              <label
                class="ec-upload"
                :class="{ 'ec-upload--busy': isUploading }"
              >
                <input
                  class="ec-upload__input"
                  type="file"
                  multiple
                  :accept="uploadAccept"
                  @change="onUploadFileChange"
                />
                <div class="ec-upload__box">
                  <span class="i-lucide-plus-circle ec-upload__plus" />
                </div>
              </label>
              <p class="ec-upload__hint">
                Max. 1500x1000 px each | png, jpg, jpeg, gif, webp | select one
                or more images
              </p>
            </div>
          </div>

          <!-- gallery accordion -->
          <div class="ec-accordion">
            <button
              class="ec-accordion__hd"
              type="button"
              @click="toggleGalleryOpen"
            >
              <span class="ec-accordion__left">
                <span class="i-lucide-image ec-accordion__icon" />
                <span class="ec-accordion__label"
                  >Browse eye-catcher gallery</span
                >
              </span>
              <span
                class="i-lucide-chevron-right ec-accordion__chev"
                :class="{ 'ec-accordion__chev--open': isGalleryOpen }"
              />
            </button>
            <div v-show="isGalleryOpen" class="ec-accordion__bd">
              <div v-if="isLoadingGallery" class="ec-gallery__loading">
                Loading…
              </div>
              <div v-else-if="galleryItems.length" class="ec-gallery">
                <button
                  v-for="item in galleryItems"
                  :key="`${item.filename}-${item.url}`"
                  type="button"
                  class="ec-gallery__item"
                  :class="{
                    'ec-gallery__item--sel': state.imageUrl === item.url,
                  }"
                  aria-label="Eye-catcher gallery item"
                  @click="selectGalleryItem(item)"
                >
                  <span
                    class="ec-gallery__delete"
                    :class="{
                      'is-disabled': isDeletingGalleryItem(item.filename),
                    }"
                    role="button"
                    tabindex="0"
                    aria-label="Delete eye-catcher"
                    @click.stop="onDeleteGalleryItem(item)"
                    @keydown.enter.stop.prevent="onDeleteGalleryItem(item)"
                    @keydown.space.stop.prevent="onDeleteGalleryItem(item)"
                  >
                    <span
                      class="i-lucide-trash-2 ec-gallery__delete-icon"
                      :class="{
                        'is-loading': isDeletingGalleryItem(item.filename),
                      }"
                    />
                  </span>
                  <span
                    v-if="state.imageUrl === item.url"
                    class="i-lucide-check ec-gallery__check"
                  />
                  <img
                    class="ec-gallery__img"
                    :src="item.url"
                    :alt="item.filename"
                  />
                </button>
              </div>
              <div v-else class="ec-gallery__empty">
                <div class="ec-gallery__empty-title">
                  No gallery items found
                </div>
                <div class="ec-gallery__empty-hint">
                  Add images (.png, .jpg, .gif, .webp) to the server gallery
                  directory, then refresh this page.
                </div>
                <div v-if="galleryError" class="ec-gallery__empty-error">
                  {{ galleryError }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- fixed save bar -->
        <div class="ec-save-bar">
          <button
            class="ec-btn ec-btn--ghost"
            type="button"
            @click="resetChanges"
          >
            Cancel
          </button>
          <button
            class="ec-btn ec-btn--primary"
            type="button"
            :disabled="saveDisabled"
            @click="saveChanges"
          >
            {{ isSaving ? 'Saving…' : 'Save changes' }}
          </button>
        </div>
      </section>

      <!-- ── RIGHT: preview (独立 scroll) ── -->
      <section class="ec-preview">
        <header class="ec-preview__hd">Preview</header>
        <div class="ec-preview__stage">
          <div class="ec-preview__scroll-inner">
            <div class="ec-preview__widget-holder">
              <div class="ec-preview__widget-stage">
                <div class="ec-preview__widget-inner">
                  <iframe
                    v-if="previewDocHtml"
                    ref="sdkFrame"
                    class="ec-preview-iframe"
                    :srcdoc="previewDocHtml"
                    title="Eye-catcher preview"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                  />
                  <div v-else class="ec-preview-empty">
                    Widget preview is unavailable for this inbox.
                  </div>
                  <img
                    v-if="state.enabled && state.imageUrl"
                    class="ec-preview__eye-overlay"
                    draggable="false"
                    :src="state.imageUrl"
                    :style="{
                      ...(isLeftPosition
                        ? {
                            left: `calc(${state.position.side}px + ${previewBubbleSide}px)`,
                          }
                        : {
                            right: `calc(${state.position.side}px + ${previewBubbleSide}px)`,
                          }),
                      bottom: `${state.position.bottom}px`,
                      cursor: 'move',
                    }"
                    @mousedown="startDrag"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="ec-preview__note">
          Drag the eye-catcher in preview to adjust its position.
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
/* ─────────────────────────────────────────────────────────────────────────
   Design tokens  (same palette as WidgetBuilder LC-override)
───────────────────────────────────────────────────────────────────────── */
.ec-page {
  --ec-accent: #0059e1;
  --ec-surface: #ffffff;
  --ec-surface2: #f6f6f7;
  --ec-border: #dde2e6;
  --ec-border-light: #eeeeef;
  --ec-text: #111827;
  --ec-text-60: #6b7280;

  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: var(--ec-surface);
  display: flex;
  flex-direction: column;
}

/* ─────────────────────────────────────────────────────────────────────────
   Outer two-column layout  (flex, no gap, fills 100% height)
───────────────────────────────────────────────────────────────────────── */
.ec-layout {
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}

/* ─────────────────────────────────────────────────────────────────────────
   LEFT COLUMN  – header (fixed) + scrollable body + save bar (fixed)
───────────────────────────────────────────────────────────────────────── */
.ec-config {
  flex: 5 1 62.5%;
  min-width: 296px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid #eeeeef;
}

/* header – never scrolls */
.ec-config__header {
  flex-shrink: 0;
  display: flex;
  width: 100%;
  height: 56px;
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #eeeeef;
}

.ec-config__title {
  margin: 0;
  height: 56px;
  line-height: 56px;
  padding-left: 21px;
  letter-spacing: 0.3px;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.ec-config__subtitle {
  margin: 0;
  padding: 0 0 20px;
  font-size: 14px;
  line-height: 22px;
  color: #6b7280;
  max-width: 760px;
}

/* scrollable area */
.ec-config__body {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 32px 32px 120px;
  scrollbar-width: thin;
}

/* save bar – never scrolls, always visible at bottom */
.ec-save-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 32px;
  border-top: 1px solid #eeeeef;
  background: var(--ec-surface);
}

/* ─────────────────────────────────────────────────────────────────────────
   RIGHT COLUMN  – preview header (fixed) + scroll stage + note (fixed)
───────────────────────────────────────────────────────────────────────── */
.ec-preview {
  box-sizing: border-box;
  height: 100%;
  border-left: 1px solid #eeeeef;
  min-width: 0;
  flex: 3 1 37.5%;
  z-index: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f5f5f5;
}

/* preview header bar */
.ec-preview__hd {
  flex-shrink: 0;
  height: 56px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  border-bottom: 1px solid #eeeeef;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  background: #f5f5f5;
}

/* scrollable preview stage */
.ec-preview__stage {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  overflow-x: hidden;
  background: #f5f5f5;
}

.ec-preview__scroll-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.ec-preview__widget-holder {
  height: 620px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  width: 360px;
  overflow: hidden;
  padding: 36px;
  margin: auto;
}

.ec-preview__widget-stage {
  flex: 1 1 100%;
  width: 100%;
  height: 100%;
  position: relative;
  display: block;
  background: transparent;
  max-width: 320px;
}

.ec-preview__widget-inner {
  position: relative;
  flex: 1 1 0%;
  height: 100%;
}

.ec-preview-iframe {
  width: 360px;
  height: 682px;
  border: none;
  display: block;
  border-radius: 16px;
  transform: scale(0.8);
  transform-origin: 0 0;
  box-shadow:
    rgba(0, 0, 0, 0.05) 0px 0.48px 2.41px -0.38px,
    rgba(0, 0, 0, 0.17) 0px 4px 20px -0.75px;
}

.ec-preview-empty {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
  text-align: center;
}

.ec-preview__eye-overlay {
  position: absolute;
  z-index: 20;
  user-select: none;
  width: auto;
  height: auto;
  max-width: min(46%, 180px);
  max-height: 120px;
  object-fit: contain;
  border-radius: 10px;
}

/* drag hint note */
.ec-preview__note {
  flex-shrink: 0;
  padding: 8px 20px 4px;
  font-size: 13px;
  color: #6b7280;
  text-align: center;
}

.ec-preview__link {
  margin-left: 6px;
  color: #0059e1;
  text-decoration: none;
}

.ec-preview__link:hover {
  text-decoration: underline;
}

/* ─────────────────────────────────────────────────────────────────────────
   Toggle card
───────────────────────────────────────────────────────────────────────── */
.ec-card {
  border: 1px solid var(--ec-border);
  border-radius: 4px;
  padding: 16px;
  background: var(--ec-surface);
  margin-bottom: 12px;
  max-width: 705px;
}

.ec-card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ec-card__title {
  font-size: 16px;
  font-weight: 600;
  color: var(--ec-text);
  margin: 0;
}

.ec-card__hint {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--ec-text-60);
}

/* ─────────────────────────────────────────────────────────────────────────
   Toggle switch
───────────────────────────────────────────────────────────────────────── */
.ec-toggle {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}

.ec-toggle__track {
  display: inline-block;
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.15);
  transition: background 0.25s cubic-bezier(0.33, 0, 0.67, 1);
}

.ec-toggle__track--on {
  background: #5dd886;
}

.ec-toggle__knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: left 0.25s cubic-bezier(0.33, 0, 0.67, 1);
}

.ec-toggle__track--on .ec-toggle__knob {
  left: 22px;
}

/* ─────────────────────────────────────────────────────────────────────────
   Accordion sections
───────────────────────────────────────────────────────────────────────── */
.ec-accordion {
  border: 1px solid var(--ec-border);
  border-radius: 4px;
  background: var(--ec-surface);
  margin-bottom: 12px;
  overflow: hidden;
  max-width: 705px;
}

.ec-accordion__hd {
  width: 100%;
  min-height: 60px;
  background: var(--ec-surface);
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  cursor: pointer;
}

.ec-accordion__hd:hover {
  background: rgba(0, 0, 0, 0.03);
}

.ec-accordion__left {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.ec-accordion__icon {
  font-size: 18px;
  color: #111827;
  width: 18px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ec-accordion__label {
  font-size: 16px;
  font-weight: 600;
  color: var(--ec-text);
}

.ec-accordion__chev {
  color: #111827;
  font-size: 16px;
  transition: transform 150ms ease;
}

.ec-accordion__chev--open {
  transform: rotate(90deg);
}

.ec-accordion__bd {
  padding: 16px;
  border-top: 1px solid var(--ec-border-light);
  background: #fff;
}

/* ─────────────────────────────────────────────────────────────────────────
   Upload area
───────────────────────────────────────────────────────────────────────── */
.ec-upload__input {
  display: none;
}

.ec-upload__box {
  width: 100%;
  height: 164px;
  border-radius: 8px;
  border: 2px dashed #bcc6d0;
  background: #f8fafc;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: border-color 150ms ease;
}

.ec-upload__box:hover {
  border-color: var(--ec-accent);
}

.ec-upload__plus {
  font-size: 44px;
  color: var(--ec-accent);
}

.ec-upload--busy {
  opacity: 0.7;
  pointer-events: none;
}

.ec-upload__hint {
  margin: 10px 0 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--ec-text-60);
}

/* ─────────────────────────────────────────────────────────────────────────
   Gallery grid
───────────────────────────────────────────────────────────────────────── */
.ec-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.ec-gallery__item {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 6px;
  border: 1px solid var(--ec-border);
  background: var(--ec-surface);
  padding: 8px;
  cursor: pointer;
  box-shadow: 0 0 0 0 transparent;
  transition:
    box-shadow 150ms ease,
    border-color 150ms ease;
  overflow: hidden;
}

.ec-gallery__item:hover {
  border-color: rgba(66, 77, 87, 0.32);
}

.ec-gallery__item--sel {
  box-shadow: 0 0 0 2px #0059e1;
  border-color: #0059e1;
}

.ec-gallery__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.ec-gallery__delete {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  color: #b91c1c;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
}

.ec-gallery__delete:hover {
  background: #fee2e2;
}

.ec-gallery__delete.is-disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.ec-gallery__delete-icon {
  font-size: 12px;
}

.ec-gallery__delete-icon.is-loading {
  animation: ec-delete-spin 1s linear infinite;
}

@keyframes ec-delete-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.ec-gallery__check {
  position: absolute;
  top: 8px;
  right: 8px;
  color: var(--ec-accent);
  background: var(--ec-surface);
  border-radius: 9999px;
  padding: 2px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
}

.ec-gallery__loading {
  font-size: 13px;
  color: var(--ec-text-60);
}

.ec-gallery__empty {
  border: 1px dashed var(--ec-border);
  border-radius: 8px;
  padding: 14px;
  background: #f8fafc;
  color: var(--ec-text);
}

.ec-gallery__empty-title {
  font-size: 13px;
  font-weight: 800;
  margin: 0 0 6px;
}

.ec-gallery__empty-hint {
  font-size: 12px;
  line-height: 1.45;
  color: var(--ec-text-60);
}

.ec-gallery__empty-error {
  margin-top: 10px;
  font-size: 12px;
  color: #b91c1c;
  word-break: break-word;
}

/* ─────────────────────────────────────────────────────────────────────────
   Buttons
───────────────────────────────────────────────────────────────────────── */
.ec-link {
  margin-left: 4px;
  color: #0059e1;
  text-decoration: none;
  font-weight: 600;
}

.ec-link:hover {
  text-decoration: underline;
}

.ec-btn {
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
}

.ec-btn--ghost {
  background: var(--ec-surface);
  border-color: #bcc6d0;
  color: #374151;
}

.ec-btn--ghost:hover {
  background: #f5f5f5;
}

.ec-btn--primary {
  background: #0059e1;
  border-color: #0059e1;
  color: #fff;
}

.ec-btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ec-btn--primary:not(:disabled):hover {
  background: #0047c2;
  border-color: #0047c2;
}

/* ─────────────────────────────────────────────────────────────────────────
   Responsive
───────────────────────────────────────────────────────────────────────── */
@media (max-width: 1240px) {
  .ec-layout {
    flex-direction: column;
  }

  .ec-config {
    flex: 1 1 auto;
    border-right: none;
    border-bottom: 1px solid var(--ec-border);
  }

  .ec-preview {
    border-left: none;
    flex: 1 1 auto;
  }
}

@media (max-width: 768px) {
  .ec-config__body {
    padding: 16px;
  }

  .ec-save-bar {
    padding: 12px 16px;
  }

  .ec-preview__hd {
    padding: 0 16px;
  }
}
</style>
